/**
 * Shared Windows-style illustrated frames for Help Video Tours.
 * Fluent-ish caption bar: app icon + title on the left, min/max/close on the RIGHT.
 */

import React from 'react';
import {
  FrameFabHero,
  FrameFabReload,
  FrameFabPlanning,
  FrameFabFilters,
  FrameFabPortfolio,
  FrameFabNext,
} from './fabHistogramTourFrames';

const FONT = '"Segoe UI", system-ui, -apple-system, sans-serif';
const DEFAULT_ACCENT = '#0369a1';

/* ------------------------------------------------------------------ */
/* Window chrome                                                       */
/* ------------------------------------------------------------------ */

export const WindowsChrome = ({ title, children, accent = DEFAULT_ACCENT }) => (
  <div style={{
    background: '#f3f3f3', borderRadius: 8, overflow: 'hidden',
    boxShadow: '0 12px 36px rgba(15, 23, 42, 0.22), 0 0 0 1px rgba(0,0,0,0.08)',
    border: '1px solid #c8c8c8', height: '100%', display: 'flex', flexDirection: 'column',
    fontFamily: FONT,
  }}>
    {/* Caption bar — Windows 11 light title bar */}
    <div style={{
      display: 'flex', alignItems: 'center', height: 32, flexShrink: 0,
      background: '#ffffff', borderBottom: '1px solid #e5e5e5',
      paddingLeft: 10,
    }}>
      <div style={{
        width: 16, height: 16, borderRadius: 3, marginRight: 8, flexShrink: 0,
        background: `linear-gradient(135deg, ${accent}, #0ea5e9)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: 9, fontWeight: 800,
      }}>
        L
      </div>
      <div style={{
        flex: 1, fontSize: 12, color: '#1a1a1a', fontWeight: 500,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        paddingRight: 8,
      }}>
        {title} — LGL Portal
      </div>
      {/* Caption buttons — RIGHT (Windows) */}
      <div style={{ display: 'flex', height: '100%', flexShrink: 0 }}>
        <CaptionBtn title="Minimize" hover="#e5e5e5">
          <svg width="10" height="10" viewBox="0 0 10 10"><path d="M1 5h8" stroke="#1a1a1a" strokeWidth="1.2" /></svg>
        </CaptionBtn>
        <CaptionBtn title="Maximize" hover="#e5e5e5">
          <svg width="10" height="10" viewBox="0 0 10 10"><rect x="1.5" y="1.5" width="7" height="7" fill="none" stroke="#1a1a1a" strokeWidth="1.2" /></svg>
        </CaptionBtn>
        <CaptionBtn title="Close" hover="#e81123" hoverColor="#fff" close>
          <svg width="10" height="10" viewBox="0 0 10 10">
            <path d="M2 2l6 6M8 2L2 8" stroke="currentColor" strokeWidth="1.2" />
          </svg>
        </CaptionBtn>
      </div>
    </div>
    <div style={{ flex: 1, position: 'relative', background: '#f3f3f3', overflow: 'hidden' }}>
      {children}
    </div>
  </div>
);

const CaptionBtn = ({ children, hover, hoverColor, close, title }) => (
  <button
    type="button"
    title={title}
    tabIndex={-1}
    style={{
      width: 46, height: '100%', border: 'none', background: 'transparent',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'default', color: '#1a1a1a', padding: 0,
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = hover;
      if (hoverColor) e.currentTarget.style.color = hoverColor;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = 'transparent';
      e.currentTarget.style.color = '#1a1a1a';
    }}
  >
    {children}
  </button>
);

export const Callout = ({ label, x, y, accent = DEFAULT_ACCENT }) => (
  <div style={{
    position: 'absolute', left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)',
    background: accent, color: 'white', padding: '6px 10px',
    borderRadius: 4, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    boxShadow: `0 6px 16px ${accent}66`, zIndex: 5, pointerEvents: 'none',
    border: '1px solid rgba(255,255,255,0.35)', fontFamily: FONT,
  }}>
    {label}
  </div>
);

/* ------------------------------------------------------------------ */
/* Shared mini widgets                                                 */
/* ------------------------------------------------------------------ */

const MiniGrid = ({ highlightCols = [], struckRows = [], extraRows = [], accent = DEFAULT_ACCENT }) => {
  const headers = ['ISO', 'Area', 'Shop %', 'Transport', 'Fit-Up', 'Bolt/Weld', 'Field %', 'Earned Hrs'];
  const rows = [
    ['ISO-1042-A', 'Area 3', '95', '100', '75', '50', '62', '184'],
    ['ISO-1043-B', 'Area 3', '65', '50', '25', '0', '18', '42'],
    ['ISO-1101-A', 'Area 5', '100', '100', '100', '75', '81', '210'],
    ['ISO-1108-C', 'Area 5', '40', '0', '0', '0', '0', '0'],
  ];
  return (
    <div style={{
      margin: 12, background: 'white', borderRadius: 4, border: '1px solid #d0d0d0',
      overflow: 'hidden', fontSize: 10, fontFamily: FONT,
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${headers.length}, 1fr)`, background: accent, color: 'white' }}>
        {headers.map((h, i) => (
          <div key={h} style={{
            padding: '6px 4px', fontWeight: 700, borderRight: '1px solid rgba(255,255,255,0.2)',
            background: highlightCols.includes(i) ? '#0284c7' : undefined,
          }}>{h}</div>
        ))}
      </div>
      {rows.map((r, ri) => {
        const struck = struckRows.includes(ri);
        const extra = extraRows.includes(ri);
        return (
          <div key={ri} style={{
            display: 'grid', gridTemplateColumns: `repeat(${headers.length}, 1fr)`,
            background: ri % 2 ? '#fafafa' : 'white',
            textDecoration: struck ? 'line-through' : 'none',
            opacity: struck ? 0.55 : 1,
            color: struck ? '#64748b' : '#1a1a1a',
          }}>
            {r.map((c, ci) => (
              <div key={ci} style={{
                padding: '5px 4px', borderTop: '1px solid #e8e8e8',
                background: highlightCols.includes(ci) ? '#e0f2fe' : (extra && ci === 0 ? '#fef3c7' : undefined),
                fontWeight: ci === 0 ? 700 : 500,
                color: ci === 0 && !struck ? accent : undefined,
              }}>
                {extra && ci === 0 ? `${c} · Extra` : c}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

const Toolbar = ({ buttons, accent }) => (
  <div style={{
    display: 'flex', gap: 6, padding: '8px 10px', background: 'white',
    borderBottom: '1px solid #e5e5e5', fontSize: 10, flexWrap: 'wrap', fontFamily: FONT,
  }}>
    {buttons.map((b) => (
      <span key={b} style={{
        padding: '4px 8px', borderRadius: 3, background: '#f5f5f5',
        border: '1px solid #d0d0d0', fontWeight: 600, color: '#333',
      }}>{b}</span>
    ))}
  </div>
);

/* ------------------------------------------------------------------ */
/* Frame templates                                                     */
/* ------------------------------------------------------------------ */

const FrameHero = ({ accent = DEFAULT_ACCENT, title = 'Help Tour' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{
      position: 'absolute', inset: 0,
      background: `linear-gradient(135deg, ${accent} 0%, #0284c7 45%, #7dd3fc 100%)`,
      display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 28,
      color: 'white', fontFamily: FONT,
    }}>
      <div style={{ fontSize: 12, letterSpacing: 1.5, opacity: 0.85, marginBottom: 8 }}>LGL PORTAL · VIDEO TOUR</div>
      <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.15, maxWidth: 440 }}>
        {title}
      </div>
      <div style={{ marginTop: 10, fontSize: 13, maxWidth: 400, opacity: 0.92, lineHeight: 1.45 }}>
        Illustrated walkthrough with narration — follow along chapter by chapter.
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
        {['Guide', 'Listen', 'Chapters'].map((t) => (
          <span key={t} style={{
            background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.35)',
            borderRadius: 3, padding: '4px 10px', fontSize: 11, fontWeight: 700,
          }}>{t}</span>
        ))}
      </div>
    </div>
  </WindowsChrome>
);

const FrameSidebar = ({ accent = DEFAULT_ACCENT, title = 'Project home' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ display: 'flex', height: '100%', fontFamily: FONT }}>
      <div style={{ width: 160, background: '#1f2937', color: '#e5e7eb', padding: 10, fontSize: 11 }}>
        <div style={{ fontWeight: 800, color: '#38bdf8', marginBottom: 10 }}>LGL</div>
        {['Home', 'Estimation', 'Progress Tracking', 'Schedule', 'Reports'].map((item) => (
          <div key={item} style={{
            padding: '6px 8px', borderRadius: 3, marginBottom: 4,
            background: item === 'Progress Tracking' ? accent : 'transparent',
            fontWeight: item === 'Progress Tracking' ? 700 : 500,
          }}>
            {item}
            {item === 'Progress Tracking' && (
              <div style={{ marginTop: 6, marginLeft: 8 }}>
                {['Piping', 'Mechanical', 'Structural'].map((s) => (
                  <div key={s} style={{
                    padding: '3px 6px', borderRadius: 2, marginBottom: 2,
                    background: s === 'Piping' ? 'rgba(255,255,255,0.2)' : 'transparent',
                    fontWeight: s === 'Piping' ? 700 : 400,
                  }}>{s}</div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ flex: 1, padding: 14 }}>
        <div style={{
          height: 28, background: 'white', border: '1px solid #d0d0d0', borderRadius: 3,
          display: 'flex', alignItems: 'center', padding: '0 10px', fontSize: 11, color: '#333', marginBottom: 12,
        }}>
          Project: <strong style={{ marginLeft: 6 }}>26-042 · Sample Job</strong>
        </div>
        <div style={{
          background: 'white', borderRadius: 4, border: '1px solid #d0d0d0', padding: 16,
          fontSize: 13, color: '#1a1a1a',
        }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>{title}</div>
          <div style={{ color: '#64748b', fontSize: 12 }}>Navigate from the left menu, then work in the main pane.</div>
        </div>
      </div>
    </div>
  </WindowsChrome>
);

const FrameMatrix = ({ accent = DEFAULT_ACCENT, title = 'Matrix view' }) => (
  <WindowsChrome title={title} accent={accent}>
    <Toolbar buttons={['Import', 'Export', 'Columns', 'Rules of Credit', 'Analytics', '❓ Help']} accent={accent} />
    <MiniGrid highlightCols={[0, 2, 6, 7]} accent={accent} />
  </WindowsChrome>
);

const FrameFreeze = ({ accent = DEFAULT_ACCENT, title = 'Rules of Credit' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ padding: 16, height: '100%', boxSizing: 'border-box', fontFamily: FONT }}>
      <div style={{
        background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 4,
        padding: 10, fontSize: 11, color: '#92400e', marginBottom: 12, fontWeight: 600,
      }}>
        Progress entry stays locked until weights are frozen for this project.
      </div>
      <div style={{ background: 'white', borderRadius: 4, border: '1px solid #d0d0d0', padding: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 8, color: '#1a1a1a' }}>Field step weights (must total 100)</div>
        {[
          ['Transport to install area', '15'],
          ['Erect supports / hangers', '20'],
          ['Fit-up', '10'],
          ['Bolt-up / weld', '35'],
          ['Line cleanup / hydro', '20'],
        ].map(([n, w]) => (
          <div key={n} style={{
            display: 'flex', justifyContent: 'space-between', padding: '6px 0',
            borderBottom: '1px solid #f0f0f0', fontSize: 12, color: '#333',
          }}>
            <span>{n}</span>
            <strong style={{ color: accent }}>{w} percent</strong>
          </div>
        ))}
        <button type="button" style={{
          marginTop: 12, width: '100%', padding: '8px 0', border: 'none', borderRadius: 3,
          background: accent, color: 'white', fontWeight: 700, fontSize: 12, fontFamily: FONT,
        }}>
          Freeze for project
        </button>
      </div>
    </div>
  </WindowsChrome>
);

const FrameShop = ({ accent = DEFAULT_ACCENT, title = 'Shop completion' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ padding: 18, fontFamily: FONT }}>
      <div style={{ fontWeight: 700, color: '#1a1a1a', marginBottom: 12 }}>Total Shop Completion</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {[
          { p: '0', l: 'Not started', c: '#f5f5f5', t: '#475569' },
          { p: '35', l: 'Boxed & cut', c: '#fef9c3', t: '#854d0e' },
          { p: '40', l: 'Issued to bench', c: '#fde68a', t: '#92400e' },
          { p: '65', l: 'Tacked', c: '#d1fae5', t: '#065f46' },
          { p: '95', l: 'Welded', c: '#dbeafe', t: '#1e40af' },
          { p: '100', l: 'Loaded / shipped', c: '#e0f2fe', t: '#0c4a6e' },
        ].map((s) => (
          <div key={s.p} style={{
            flex: '1 1 90px', background: s.c, color: s.t, borderRadius: 4,
            padding: 10, textAlign: 'center', border: '1px solid rgba(0,0,0,0.06)',
          }}>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{s.p}</div>
            <div style={{ fontSize: 10, fontWeight: 600, marginTop: 2 }}>{s.l}</div>
          </div>
        ))}
      </div>
      <MiniGrid highlightCols={[2]} accent={accent} />
    </div>
  </WindowsChrome>
);

const FrameField = ({ accent = DEFAULT_ACCENT, title = 'Field progress' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ padding: 12, fontFamily: FONT }}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginBottom: 10, fontSize: 10,
      }}>
        {[
          ['Transport', '15%'],
          ['Supports', '20%'],
          ['Fit-up', '10%'],
          ['Bolt/Weld', '35%'],
          ['Cleanup', '20%'],
        ].map(([n, w]) => (
          <div key={n} style={{
            background: 'white', border: `1px solid ${accent}44`, borderRadius: 4, padding: 8, textAlign: 'center',
          }}>
            <div style={{ fontWeight: 700, color: accent }}>{n}</div>
            <div style={{ color: accent, fontWeight: 800 }}>{w}</div>
          </div>
        ))}
      </div>
      <MiniGrid highlightCols={[3, 4, 5, 6]} accent={accent} />
    </div>
  </WindowsChrome>
);

const FrameExcel = ({ accent = DEFAULT_ACCENT, title = 'Spreadsheet editing' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ position: 'relative', height: '100%' }}>
      <MiniGrid highlightCols={[4]} accent={accent} />
      <div style={{
        position: 'absolute', left: '38%', top: '58%', width: 14, height: 14,
        background: accent, border: '2px solid white', borderRadius: 2,
        boxShadow: `0 0 0 3px ${accent}55`,
      }} />
      <div style={{
        position: 'absolute', left: '42%', top: '58%', width: 2, height: 48,
        background: `${accent}88`,
      }} />
    </div>
  </WindowsChrome>
);

const FrameIO = ({ accent = DEFAULT_ACCENT, title = 'Import / Export' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{
      padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
      height: '100%', boxSizing: 'border-box', fontFamily: FONT,
    }}>
      <div style={{
        background: 'white', border: `2px dashed ${accent}88`, borderRadius: 4, padding: 14,
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
      }}>
        <div style={{ fontSize: 28, marginBottom: 6 }}>📤</div>
        <div style={{ fontWeight: 700, color: '#1a1a1a' }}>Import Excel</div>
        <div style={{ fontSize: 11, color: '#64748b', textAlign: 'center', marginTop: 6 }}>
          Bring source data in. Progress you already entered stays safe.
        </div>
      </div>
      <div style={{
        background: 'white', border: '1px solid #d0d0d0', borderRadius: 4, padding: 14,
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
      }}>
        <div style={{ fontSize: 28, marginBottom: 6 }}>📥</div>
        <div style={{ fontWeight: 700, color: '#1a1a1a' }}>Export Excel</div>
        <div style={{ fontSize: 11, color: '#64748b', textAlign: 'center', marginTop: 6 }}>
          Export what you see — or everything, including hidden fields.
        </div>
      </div>
    </div>
  </WindowsChrome>
);

const FrameTrends = ({ accent = DEFAULT_ACCENT, title = 'Progress trends' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ display: 'flex', height: '100%', fontFamily: FONT }}>
      <div style={{ width: '42%' }}><MiniGrid highlightCols={[0]} accent={accent} /></div>
      <div style={{
        flex: 1, margin: 12, background: 'white', borderRadius: 4, border: '1px solid #d0d0d0',
        padding: 12,
      }}>
        <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 8, color: '#1a1a1a' }}>Trend over time</div>
        <svg viewBox="0 0 220 100" width="100%" height="110">
          <polyline
            fill="none" stroke={accent} strokeWidth="3"
            points="10,90 50,78 90,60 130,48 170,28 210,18"
          />
          {[10, 50, 90, 130, 170, 210].map((x, i) => (
            <circle key={x} cx={x} cy={[90, 78, 60, 48, 28, 18][i]} r="4" fill={accent} />
          ))}
        </svg>
        <div style={{ fontSize: 10, color: '#64748b' }}>Week-over-week snapshots from history</div>
      </div>
    </div>
  </WindowsChrome>
);

const FrameCollab = ({ accent = DEFAULT_ACCENT, title = 'Collaboration' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ padding: 12, fontFamily: FONT }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, marginBottom: 8 }}>
        {['AP', 'JM', 'SK'].map((ini, i) => (
          <div key={ini} style={{
            width: 26, height: 26, borderRadius: '50%', background: [accent, '#0f766e', '#b45309'][i],
            color: 'white', fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid white', marginLeft: i ? -6 : 0,
          }}>{ini}</div>
        ))}
      </div>
      <MiniGrid accent={accent} />
      <div style={{
        marginTop: 8, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 4,
        padding: 8, fontSize: 11, color: '#92400e',
      }}>
        Comment: <strong>@Jordan</strong> can you confirm before we clear the hold?
      </div>
    </div>
  </WindowsChrome>
);

const FrameSchedule = ({ accent = DEFAULT_ACCENT, title = 'Schedule link' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{
      padding: 12, display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 10,
      height: '100%', boxSizing: 'border-box', fontFamily: FONT,
    }}>
      <MiniGrid highlightCols={[0]} accent={accent} />
      <div style={{ background: 'white', borderRadius: 4, border: '1px solid #d0d0d0', padding: 10, fontSize: 11 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Schedule Instances</div>
        {[
          ['A1020 · Area 3 install', '62%'],
          ['A1044 · Area 5 install', '41%'],
          ['A1100 · Hydro package', '18%'],
        ].map(([n, p]) => (
          <div key={n} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span>{n}</span><strong style={{ color: accent }}>{p}</strong>
            </div>
            <div style={{ height: 6, background: '#e5e5e5', borderRadius: 2 }}>
              <div style={{ width: p, height: '100%', background: accent, borderRadius: 2 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  </WindowsChrome>
);

const FrameAnalytics = ({ accent = DEFAULT_ACCENT, title = 'Analytics' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ padding: 12, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, fontFamily: FONT }}>
      {[
        ['Complete', '54%', accent],
        ['Earned hours', '12,480', '#0f766e'],
        ['On hold', '7 rows', '#b45309'],
      ].map(([l, v, c]) => (
        <div key={l} style={{
          background: 'white', borderRadius: 4, border: '1px solid #e5e5e5', padding: 12, textAlign: 'center',
        }}>
          <div style={{ fontSize: 11, color: '#64748b' }}>{l}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: c }}>{v}</div>
        </div>
      ))}
    </div>
    <div style={{
      margin: '0 12px', background: 'white', borderRadius: 4, border: '1px solid #e5e5e5',
      padding: 10, fontSize: 11, fontFamily: FONT,
    }}>
      Downstream reports stay live from the data you enter here.
    </div>
  </WindowsChrome>
);

const FrameSisters = ({ accent = DEFAULT_ACCENT, title = 'Related views' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ padding: 16, fontFamily: FONT }}>
      <div style={{
        display: 'inline-flex', background: 'white', border: '1px solid #d0d0d0',
        borderRadius: 4, overflow: 'hidden', marginBottom: 14, fontSize: 12, fontWeight: 700,
      }}>
        {['Progress', 'NDE', 'Testing'].map((t, i) => (
          <div key={t} style={{
            padding: '8px 14px',
            background: i === 0 ? accent : 'white',
            color: i === 0 ? 'white' : '#333',
            borderLeft: i ? '1px solid #e5e5e5' : 'none',
          }}>{t}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 4, padding: 12 }}>
          <div style={{ fontWeight: 700, color: accent, marginBottom: 4 }}>Sister view A</div>
          <div style={{ fontSize: 11, color: '#334155' }}>
            Related tracker under the same family — same project context, different columns.
          </div>
        </div>
        <div style={{ background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 4, padding: 12 }}>
          <div style={{ fontWeight: 700, color: '#0f766e', marginBottom: 4 }}>Sister view B</div>
          <div style={{ fontSize: 11, color: '#334155' }}>
            Jump between views without leaving the project.
          </div>
        </div>
      </div>
    </div>
  </WindowsChrome>
);

const FrameReissue = ({ accent = DEFAULT_ACCENT, title = 'Re-issue' }) => (
  <WindowsChrome title={title} accent={accent}>
    <MiniGrid struckRows={[1]} extraRows={[2]} accent={accent} />
    <div style={{
      margin: '0 12px', background: '#fff7ed', border: '1px solid #fdba74',
      borderRadius: 4, padding: 10, fontSize: 11, color: '#9a3412', fontFamily: FONT,
    }}>
      Old row stays for history. New Extra row carries the updated quantities.
    </div>
  </WindowsChrome>
);

const FrameChecklist = ({ accent = DEFAULT_ACCENT, title = 'Checklist' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ padding: 18, fontFamily: FONT }}>
      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 12, color: '#1a1a1a' }}>Getting started</div>
      {[
        'Confirm prerequisites are set for this project',
        'Filter the view to your area or responsibility',
        'Update values carefully — reports depend on them',
        'Use Import for bulk source data when needed',
        'Open history when you need the story behind a row',
        'Ask before clearing a hold or editing superseded data',
      ].map((step, i) => (
        <div key={step} style={{
          display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8,
          background: 'white', border: '1px solid #e5e5e5', borderRadius: 4, padding: '8px 10px',
        }}>
          <span style={{
            width: 22, height: 22, borderRadius: 3, background: accent, color: 'white',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 800, flexShrink: 0,
          }}>{i + 1}</span>
          <span style={{ fontSize: 12, color: '#333', paddingTop: 2 }}>{step}</span>
        </div>
      ))}
    </div>
  </WindowsChrome>
);

const FrameForm = ({ accent = DEFAULT_ACCENT, title = 'Form' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ padding: 18, fontFamily: FONT }}>
      <div style={{
        background: 'white', border: '1px solid #d0d0d0', borderRadius: 4, padding: 16, maxWidth: 420,
      }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: '#1a1a1a' }}>Details</div>
        {[
          ['Name', 'Sample item'],
          ['Status', 'Active'],
          ['Owner', 'Jordan M.'],
          ['Updated', 'Today'],
        ].map(([l, v]) => (
          <div key={l} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>{l}</div>
            <div style={{
              border: '1px solid #d0d0d0', borderRadius: 3, padding: '7px 10px',
              fontSize: 12, background: '#fafafa', color: '#1a1a1a',
            }}>{v}</div>
          </div>
        ))}
        <button type="button" style={{
          marginTop: 4, padding: '8px 16px', border: 'none', borderRadius: 3,
          background: accent, color: 'white', fontWeight: 700, fontSize: 12, fontFamily: FONT,
        }}>
          Save
        </button>
      </div>
    </div>
  </WindowsChrome>
);

const FrameDashboard = ({ accent = DEFAULT_ACCENT, title = 'Dashboard' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ padding: 12, fontFamily: FONT }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 10 }}>
        {[
          ['Open', '24'],
          ['In progress', '11'],
          ['Complete', '63%'],
          ['Alerts', '3'],
        ].map(([l, v]) => (
          <div key={l} style={{
            background: 'white', border: '1px solid #e5e5e5', borderRadius: 4, padding: 10, textAlign: 'center',
          }}>
            <div style={{ fontSize: 10, color: '#64748b' }}>{l}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: accent }}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{
        background: 'white', border: '1px solid #e5e5e5', borderRadius: 4, padding: 12, height: 120,
        display: 'flex', alignItems: 'flex-end', gap: 6,
      }}>
        {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
          <div key={i} style={{
            flex: 1, height: `${h}%`, background: i === 5 ? accent : '#bae6fd', borderRadius: '3px 3px 0 0',
          }} />
        ))}
      </div>
    </div>
  </WindowsChrome>
);

/* ------------------------------------------------------------------ */
/* GA Dashboards frames (curated tour)                                 */
/* ------------------------------------------------------------------ */

const GA_ACCENT = '#0f766e';
const GA_STATUS = {
  grey: '#94a3b8',
  amber: '#d97706',
  green: '#16a34a',
  red: '#dc2626',
};

/** Mini site-plan drawing with colored marker pins. */
const GaDrawingPad = ({
  showBalloons = true,
  showLeaders = true,
  editTools = false,
  highlightRev = false,
  presentMode = false,
  accent = GA_ACCENT,
}) => {
  const markers = [
    { id: 'stack', label: 'Stack', color: GA_STATUS.amber, ax: 58, ay: 38, lx: 8, ly: 22, start: '12-Mar-26 A', finish: '28-Apr-26', pct: '62%', units: 'Bud 420 · Act 260 · Rem 160' },
    { id: 'gtg', label: 'GTG enclosure', color: GA_STATUS.green, ax: 36, ay: 52, lx: 8, ly: 58, start: '02-Feb-26 A', finish: '18-Mar-26 A', pct: '100%', units: 'Bud 880 · Act 880 · Rem 0' },
    { id: 'fnd', label: 'Foundations', color: GA_STATUS.red, ax: 72, ay: 68, lx: 88, ly: 72, start: '20-Jan-26 A', finish: '05-Mar-26', pct: '71%', units: 'Bud 1,200 · Act 850 · Rem 350' },
    { id: 'air', label: 'Air intake', color: GA_STATUS.grey, ax: 44, ay: 28, lx: 88, ly: 18, start: '01-May-26', finish: '20-Jun-26', pct: '0%', units: 'Bud 310 · Act 0 · Rem 310' },
  ];

  // Pin coords (ax,ay) are % of the center drawing; convert to stage % when gutters (20/60/20) are on.
  const pinToStage = (ax, ay) => (showBalloons ? { x: 20 + ax * 0.6, y: ay } : { x: ax, y: ay });

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%', fontFamily: FONT,
      background: '#e2e8f0',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
        padding: '6px 8px', background: presentMode ? '#0f172a' : '#fff',
        borderBottom: '1px solid #cbd5e1', fontSize: 10, flexShrink: 0,
      }}>
        {!presentMode && <span style={{ fontWeight: 800, color: accent, marginRight: 4 }}>← Dashboards</span>}
        <strong style={{ color: presentMode ? '#fff' : '#0f172a', fontSize: 11 }}>Overall Site Plan</strong>
        <span style={{
          padding: '2px 7px', borderRadius: 4, fontWeight: 800, fontSize: 10,
          background: highlightRev ? '#fef08a' : '#f0fdfa',
          border: `1px solid ${highlightRev ? '#eab308' : '#99f6e4'}`,
          color: highlightRev ? '#854d0e' : accent,
        }}>
          Rev · 26LG0052-Current (live)
        </span>
        <span style={{
          padding: '2px 6px', borderRadius: 4, border: '1px solid #cbd5e1',
          background: presentMode ? '#1e293b' : '#fff', color: presentMode ? '#cbd5e1' : '#475569',
        }}>
          Compare · vs 20260727
        </span>
        <span style={{
          padding: '2px 6px', borderRadius: 4, border: '1px solid #cbd5e1',
          background: presentMode ? '#1e293b' : '#fff', color: presentMode ? '#cbd5e1' : '#475569',
        }}>
          Units: task
        </span>
        <div style={{ flex: 1 }} />
        {editTools && (
          <span style={{ padding: '2px 7px', borderRadius: 4, background: accent, color: '#fff', fontWeight: 800 }}>✓ Editing</span>
        )}
        <span style={{
          padding: '2px 6px', borderRadius: 4, border: '1px solid #cbd5e1',
          background: presentMode ? '#1e293b' : '#fff', color: presentMode ? '#e2e8f0' : '#334155', fontWeight: 700,
        }}>
          {presentMode ? 'Exit' : 'Present'}
        </span>
        {!presentMode && (
          <span style={{ padding: '2px 6px', borderRadius: 4, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontWeight: 700 }}>
            PDF
          </span>
        )}
      </div>

      {editTools && (
        <div style={{
          display: 'flex', gap: 6, padding: '5px 8px', background: '#f0fdfa',
          borderBottom: '1px solid #99f6e4', fontSize: 10, color: '#0f766e', fontWeight: 700, flexShrink: 0,
        }}>
          <span style={{ padding: '2px 6px', background: accent, color: '#fff', borderRadius: 4 }}>Pin</span>
          <span style={{ padding: '2px 6px', border: '1px solid #99f6e4', borderRadius: 4, background: '#fff' }}>Zone</span>
          <span style={{ padding: '2px 6px', border: '1px solid #99f6e4', borderRadius: 4, background: '#fff' }}>Polygon</span>
          <span style={{ padding: '2px 6px', border: '1px solid #fde68a', borderRadius: 4, background: '#fffbeb', color: '#92400e' }}>Bind to WBS…</span>
        </div>
      )}

      <div style={{
        flex: 1, minHeight: 0, position: 'relative',
        display: 'grid', gridTemplateColumns: showBalloons ? '20% 60% 20%' : '1fr',
      }}>
        {showBalloons && <div style={{ background: '#f8fafc', borderRight: '1px solid #e2e8f0' }} />}
        <div style={{ position: 'relative', overflow: 'hidden', background: '#cbd5e1' }}>
          <div style={{
            position: 'absolute', inset: 10, borderRadius: 4, background: '#f1f5f9',
            border: '1px solid #94a3b8',
            backgroundImage: `
              linear-gradient(rgba(148,163,184,0.35) 1px, transparent 1px),
              linear-gradient(90deg, rgba(148,163,184,0.35) 1px, transparent 1px)
            `,
            backgroundSize: '18px 18px',
          }}>
            <div style={{
              position: 'absolute', left: '28%', top: '40%', width: '22%', height: '28%',
              border: `2px solid ${accent}`, borderRadius: 6, background: 'rgba(15,118,110,0.08)',
            }} />
            <div style={{
              position: 'absolute', left: '52%', top: '28%', width: '14%', height: '36%',
              border: '2px solid #64748b', borderRadius: '50% 50% 4px 4px', background: 'rgba(100,116,139,0.12)',
            }} />
            <div style={{
              position: 'absolute', left: '64%', top: '58%', width: '20%', height: '16%',
              border: '2px solid #b45309', borderRadius: 3, background: 'rgba(180,83,9,0.1)',
            }} />
            <div style={{
              position: 'absolute', left: '12%', bottom: 8, fontSize: 9, color: '#64748b', fontWeight: 700,
            }}>
              C005 · Site Plan (sample)
            </div>
          </div>
          {editTools && !showBalloons && markers.slice(0, 2).map((m) => (
            <div key={`el-${m.id}`} style={{
              position: 'absolute', left: `${m.ax}%`, top: `${Math.max(8, m.ay - 10)}%`, transform: 'translate(-50%, -100%)',
              background: '#fff', border: `2px solid ${m.color}`, borderRadius: 5, padding: '2px 5px',
              fontSize: 9, fontWeight: 700, whiteSpace: 'nowrap', boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            }}>
              {m.label}
            </div>
          ))}
        </div>
        {showBalloons && <div style={{ background: '#f8fafc', borderLeft: '1px solid #e2e8f0' }} />}

        <svg
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 2 }}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          {markers.map((m) => {
            const pin = pinToStage(m.ax, m.ay);
            const leftGutter = m.lx < 50;
            return (
              <g key={`g-${m.id}`}>
                {showLeaders && showBalloons && (
                  <line
                    x1={pin.x} y1={pin.y}
                    x2={leftGutter ? 10 : 90} y2={m.ly}
                    stroke={m.color} strokeWidth="0.45" opacity="0.9"
                  />
                )}
                <circle cx={pin.x} cy={pin.y} r="1.3" fill={m.color} stroke="#fff" strokeWidth="0.35" />
              </g>
            );
          })}
        </svg>

        {showBalloons && markers.map((m) => {
          const leftGutter = m.lx < 50;
          return (
            <div
              key={`b-${m.id}`}
              style={{
                position: 'absolute',
                left: leftGutter ? '1%' : 'auto',
                right: leftGutter ? 'auto' : '1%',
                top: `${m.ly}%`,
                transform: 'translateY(-50%)',
                width: 102,
                background: 'rgba(255,255,255,0.97)',
                border: `2px solid ${m.color}`,
                borderRadius: 6,
                padding: '3px 5px',
                fontSize: 9,
                lineHeight: 1.25,
                boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                zIndex: 3,
                pointerEvents: 'none',
              }}
            >
              <div style={{ fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: m.color, flexShrink: 0 }} />
                {m.label}
                <span style={{ color: '#475569', fontWeight: 700 }}>{m.pct}</span>
              </div>
              <div style={{ color: '#334155' }}>{m.start} → {m.finish}</div>
              <div style={{ color: '#64748b', fontSize: 8 }}>{m.units}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FrameGaHero = ({ accent = GA_ACCENT, title = 'GA Dashboards' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ position: 'relative', height: '100%' }}>
      <GaDrawingPad accent={accent} showBalloons showLeaders />
      <div style={{
        position: 'absolute', left: 12, bottom: 12, background: 'rgba(15,23,42,0.82)',
        color: '#fff', borderRadius: 6, padding: '8px 10px', fontSize: 11, fontFamily: FONT, maxWidth: 260,
      }}>
        One drawing. Live P6 dates and labor units on every area.
      </div>
    </div>
  </WindowsChrome>
);

const FrameGaToolbar = ({ accent = GA_ACCENT, title = 'Rev and units' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ position: 'relative', height: '100%' }}>
      <GaDrawingPad accent={accent} highlightRev showBalloons={false} showLeaders={false} />
      <div style={{
        position: 'absolute', left: '50%', top: 44, transform: 'translateX(-50%)',
        background: '#fff', border: '2px solid #eab308', borderRadius: 8, padding: '10px 12px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)', fontFamily: FONT, fontSize: 11, zIndex: 6, minWidth: 280,
      }}>
        <div style={{ fontWeight: 800, color: '#854d0e', marginBottom: 6 }}>Toolbar · schedule source</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, color: '#334155' }}>
          <div><strong style={{ color: accent }}>Rev</strong> — live Current or dated XER copy from P6</div>
          <div><strong>Compare</strong> — slip days vs another Rev / snapshot</div>
          <div><strong>%</strong> — weighted (Act÷Bud) or average of phys %</div>
          <div><strong>All balloon data</strong> — from P6 (not progress trackers)</div>
        </div>
      </div>
    </div>
  </WindowsChrome>
);

const FrameGaEdit = ({ accent = GA_ACCENT, title = 'Edit and bind' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ position: 'relative', height: '100%' }}>
      <GaDrawingPad accent={accent} editTools showBalloons={false} showLeaders={false} />
      <div style={{
        position: 'absolute', right: 10, top: 70, width: 160,
        background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 10,
        fontFamily: FONT, fontSize: 10, color: '#78350f', zIndex: 6, boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
      }}>
        <div style={{ fontWeight: 800, marginBottom: 4 }}>Selected marker</div>
        <div style={{ marginBottom: 6 }}>Stack · bound to WBS</div>
        <div style={{ fontWeight: 700, color: accent, marginBottom: 6 }}>🔗 4 activities</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {['Dates', '%', 'Units', 'Baseline'].map((t, i) => (
            <span key={t} style={{
              padding: '2px 5px', borderRadius: 4, fontWeight: 700, fontSize: 9,
              background: i < 3 ? accent : '#fff', color: i < 3 ? '#fff' : '#78716c',
              border: '1px solid #cbd5e1',
            }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  </WindowsChrome>
);

const FrameGaBalloons = ({ accent = GA_ACCENT, title = 'Balloons' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ position: 'relative', height: '100%' }}>
      <GaDrawingPad accent={accent} showBalloons showLeaders />
      <div style={{
        position: 'absolute', left: '50%', bottom: 10, transform: 'translateX(-50%)',
        display: 'flex', gap: 8, background: 'rgba(255,255,255,0.95)', border: '1px solid #cbd5e1',
        borderRadius: 8, padding: '6px 10px', fontFamily: FONT, fontSize: 10, fontWeight: 700, zIndex: 6,
      }}>
        {[
          [GA_STATUS.grey, 'Not started'],
          [GA_STATUS.amber, 'In progress'],
          [GA_STATUS.green, 'Complete'],
          [GA_STATUS.red, 'Late'],
        ].map(([c, t]) => (
          <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#334155' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: c }} />
            {t}
          </span>
        ))}
        <span style={{ color: '#64748b', fontWeight: 600 }}>· A = actual</span>
      </div>
    </div>
  </WindowsChrome>
);

const FrameGaPresent = ({ accent = GA_ACCENT, title = 'Present and export' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ position: 'relative', height: '100%' }}>
      <GaDrawingPad accent={accent} presentMode showBalloons showLeaders />
      <div style={{
        position: 'absolute', left: 10, right: 10, bottom: 10,
        background: '#fff', border: `1px solid ${accent}`, borderRadius: 8, padding: '8px 10px',
        display: 'flex', alignItems: 'center', gap: 10, fontFamily: FONT, fontSize: 10, zIndex: 6,
        boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
      }}>
        <strong style={{ color: accent }}>▶ Play</strong>
        <div style={{ flex: 1, height: 6, background: '#e2e8f0', borderRadius: 3, position: 'relative' }}>
          <div style={{ width: '45%', height: '100%', background: accent, borderRadius: 3 }} />
          <div style={{
            position: 'absolute', left: '45%', top: -3, width: 12, height: 12,
            borderRadius: '50%', background: accent, border: '2px solid #fff',
          }} />
        </div>
        <span style={{ color: '#64748b', fontWeight: 700 }}>Week scrubber</span>
        <span style={{ padding: '3px 7px', background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 4, color: accent, fontWeight: 800 }}>PNG</span>
        <span style={{ padding: '3px 7px', background: accent, color: '#fff', borderRadius: 4, fontWeight: 800 }}>PDF all slides</span>
      </div>
    </div>
  </WindowsChrome>
);

const FrameGaPress = ({ accent = GA_ACCENT, title = 'Press Progress' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ padding: 12, fontFamily: FONT, height: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <strong style={{ color: accent }}>Press Progress · 75998</strong>
        <span style={{ padding: '2px 7px', borderRadius: 4, background: accent, color: '#fff', fontSize: 10, fontWeight: 800 }}>Overall</span>
        <span style={{ padding: '2px 7px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 10, color: '#64748b' }}>Last Week</span>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: '#854d0e', background: '#fef9c3', border: '1px solid #eab308', borderRadius: 4, padding: '2px 6px', fontWeight: 700 }}>
          Rev · Current (live)
        </span>
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: '28px repeat(5, 1fr)', gap: 3, fontSize: 9,
      }}>
        <div />
        {['MECH', 'ELEC', 'PIPE', 'PUN', 'TCCC'].map((h) => (
          <div key={h} style={{ textAlign: 'center', fontWeight: 800, color: '#64748b' }}>{h}</div>
        ))}
        {['J', 'K', 'L'].map((row) => (
          <React.Fragment key={row}>
            <div style={{ fontWeight: 800, color: accent, display: 'flex', alignItems: 'center' }}>{row}</div>
            {[72, 55, 40, 100, 100].map((v, ci) => (
              <div key={`${row}-${ci}`} style={{
                background: v >= 100 ? '#dcfce7' : v > 50 ? '#ffedd5' : '#f1f5f9',
                border: '1px solid #e2e8f0', borderRadius: 3, textAlign: 'center', padding: '6px 2px',
                fontWeight: 700, color: '#0f172a',
              }}>
                {v}%
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
      <div style={{ marginTop: 10, fontSize: 10, color: '#64748b' }}>
        Companion card on the GA Dashboards list for Goodyear Polar Press (job 75998).
      </div>
    </div>
  </WindowsChrome>
);

/* ------------------------------------------------------------------ */
/* Time Tracker frames                                                 */
/* ------------------------------------------------------------------ */

const TT_ACCENT = '#0d9488';

const FrameTtHero = ({ accent = TT_ACCENT, title = 'Time Tracker' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{
      position: 'absolute', inset: 0,
      background: `linear-gradient(135deg, ${accent} 0%, #0f766e 50%, #115e59 100%)`,
      display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 28,
      color: 'white', fontFamily: FONT,
    }}>
      <div style={{ fontSize: 12, letterSpacing: 1.5, opacity: 0.85, marginBottom: 8 }}>LGL PORTAL · TIME TRACKER</div>
      <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.15, maxWidth: 460 }}>
        Hours that payroll can trust
      </div>
      <div style={{ marginTop: 10, fontSize: 13, maxWidth: 420, opacity: 0.92, lineHeight: 1.45 }}>
        Labour codes first. Then hours. Then payroll, actuals, and forecasting.
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
        {['Daily', 'Weekly', 'Op Codes', 'Extras'].map((t) => (
          <span key={t} style={{
            background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.35)',
            borderRadius: 4, padding: '4px 10px', fontSize: 11, fontWeight: 700,
          }}>{t}</span>
        ))}
      </div>
    </div>
  </WindowsChrome>
);

const FrameTtDailyWeekly = ({ accent = TT_ACCENT, title = 'Daily or weekly' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{
      padding: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
      height: '100%', boxSizing: 'border-box', fontFamily: FONT,
    }}>
      <div style={{ background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 8, padding: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: accent, letterSpacing: 0.6 }}>DAILY</div>
        <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a', margin: '4px 0 8px' }}>Crew heads · one day</div>
        {['Crew roster', 'One date', 'Row per person + code'].map((l) => (
          <div key={l} style={{
            fontSize: 11, background: '#fff', border: '1px solid #ccfbf1',
            borderRadius: 4, padding: '6px 8px', marginBottom: 6, fontWeight: 600, color: '#334155',
          }}>{l}</div>
        ))}
      </div>
      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: '#2563eb', letterSpacing: 0.6 }}>WEEKLY</div>
        <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a', margin: '4px 0 8px' }}>One person · seven days</div>
        {['CA Sun–Sat · US Mon–Sun', 'Hours grid auto-saves', 'Submit when the week is done'].map((l) => (
          <div key={l} style={{
            fontSize: 11, background: '#fff', border: '1px solid #dbeafe',
            borderRadius: 4, padding: '6px 8px', marginBottom: 6, fontWeight: 600, color: '#334155',
          }}>{l}</div>
        ))}
      </div>
    </div>
  </WindowsChrome>
);

const FrameTtPipeline = ({ accent = TT_ACCENT, title = 'Labour-code path' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{
      padding: 16, height: '100%', boxSizing: 'border-box', fontFamily: FONT,
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
    }}>
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 6 }}>
        {[
          ['1', 'Maestro', 'Coding Structure', '#f8fafc', '#e2e8f0', '#475569'],
          ['2', 'Admin load', 'Job + discipline', '#eff6ff', '#bfdbfe', '#2563eb'],
          ['3', 'Dropdown', 'Active codes only', '#f0fdfa', '#99f6e4', accent],
          ['4', 'Hours save', 'Description stamps', '#fffbeb', '#fde68a', '#d97706'],
        ].map(([n, t, s, bg, bd, c], i) => (
          <React.Fragment key={t}>
            <div style={{
              flex: 1, background: bg, border: `1px solid ${bd}`, borderRadius: 8,
              padding: '12px 8px', textAlign: 'center',
            }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%', background: c, color: '#fff',
                fontSize: 11, fontWeight: 800, display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', marginBottom: 6,
              }}>{n}</div>
              <div style={{ fontWeight: 800, fontSize: 12, color: '#0f172a' }}>{t}</div>
              <div style={{ fontSize: 10, color: '#64748b', marginTop: 3 }}>{s}</div>
            </div>
            {i < 3 && (
              <div style={{ alignSelf: 'center', color: '#94a3b8', fontWeight: 800, fontSize: 16 }}>→</div>
            )}
          </React.Fragment>
        ))}
      </div>
      <div style={{
        marginTop: 12, background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 6,
        padding: '8px 10px', fontSize: 11, color: '#9a3412', fontWeight: 600,
      }}>
        Empty dropdown = that job-discipline was never loaded. Time Tracker does not invent codes.
      </div>
    </div>
  </WindowsChrome>
);

const FrameTtDropdown = ({ accent = TT_ACCENT, title = 'Pick a labour code' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ padding: 14, fontFamily: FONT, height: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <div style={{
          flex: 1, background: '#fff', border: `2px solid ${accent}`, borderRadius: 6,
          padding: '8px 10px', fontSize: 12, fontWeight: 700, color: '#0f172a',
        }}>
          99999 PFS
        </div>
        <div style={{
          flex: 1.4, background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 6,
          padding: '6px 8px', display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{
            background: accent, color: '#fff', borderRadius: 4, padding: '2px 7px',
            fontSize: 11, fontWeight: 800, fontFamily: 'ui-monospace, Consolas, monospace',
          }}>25001</span>
          <span style={{ fontSize: 11, color: '#334155', fontWeight: 600 }}>Pipe fab — first operation</span>
          <span style={{ marginLeft: 'auto', color: '#94a3b8', fontWeight: 700 }}>×</span>
        </div>
      </div>
      <div style={{
        background: '#fff', border: '1px solid #d0d0d0', borderRadius: 6, overflow: 'hidden', fontSize: 11,
      }}>
        {[
          ['25001', 'Pipe fab — first operation', false],
          ['25014', 'Fit-up and tack', false],
          ['85001', 'Extra — field support', true],
        ].map(([code, desc, extra]) => (
          <div key={code} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
            borderTop: '1px solid #f1f5f9', background: code === '25001' ? '#ecfdf5' : '#fff',
          }}>
            <span style={{
              fontFamily: 'ui-monospace, Consolas, monospace', fontWeight: 800, color: accent, width: 48,
            }}>{code}</span>
            <span style={{ color: '#334155', flex: 1 }}>{desc}</span>
            {extra && (
              <span style={{
                background: '#f59e0b', color: '#fff', fontSize: 9, fontWeight: 800,
                borderRadius: 3, padding: '2px 6px',
              }}>EXTRA</span>
            )}
          </div>
        ))}
      </div>
    </div>
  </WindowsChrome>
);

const FrameTtAdmin = ({ accent = TT_ACCENT, title = 'Op Codes and Extras' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ padding: 14, fontFamily: FONT, height: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
        {[
          ['📋', 'Op Codes', 'Standard labour', '#f0fdfa', '#99f6e4'],
          ['🔶', 'Extras', 'Change-order codes', '#fffbeb', '#fde68a'],
          ['👥', 'Employees', 'Master list', '#eff6ff', '#bfdbfe'],
        ].map(([icon, t, s, bg, bd]) => (
          <div key={t} style={{
            background: bg, border: `1px solid ${bd}`, borderRadius: 8, padding: 10, textAlign: 'center',
          }}>
            <div style={{ fontSize: 18 }}>{icon}</div>
            <div style={{ fontWeight: 800, fontSize: 12, color: '#0f172a', marginTop: 4 }}>{t}</div>
            <div style={{ fontSize: 10, color: '#64748b' }}>{s}</div>
          </div>
        ))}
      </div>
      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', fontSize: 11,
      }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '80px 1fr 70px', background: accent, color: '#fff', fontWeight: 800,
        }}>
          <div style={{ padding: '6px 8px' }}>Code</div>
          <div style={{ padding: '6px 8px' }}>Description</div>
          <div style={{ padding: '6px 8px' }}>Status</div>
        </div>
        {[
          ['25001', 'Pipe fab — first operation', true],
          ['25014', 'Fit-up and tack', true],
          ['01230', 'Safety orientation', false],
        ].map(([c, d, on]) => (
          <div key={c} style={{
            display: 'grid', gridTemplateColumns: '80px 1fr 70px', borderTop: '1px solid #f1f5f9',
            opacity: on ? 1 : 0.55, textDecoration: on ? 'none' : 'line-through',
          }}>
            <div style={{ padding: '6px 8px', fontFamily: 'ui-monospace, Consolas, monospace', fontWeight: 700 }}>{c}</div>
            <div style={{ padding: '6px 8px', color: '#334155' }}>{d}</div>
            <div style={{ padding: '6px 8px', color: on ? '#059669' : '#94a3b8', fontWeight: 700 }}>
              {on ? '● Active' : '○ Off'}
            </div>
          </div>
        ))}
      </div>
    </div>
  </WindowsChrome>
);

const FrameTtSaved = ({ accent = TT_ACCENT, title = 'After you save' }) => (
  <WindowsChrome title={title} accent={accent}>
    <div style={{ padding: 14, fontFamily: FONT, height: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 10 }}>
        {[
          ['Payroll export', 'Maestro 9-line', accent],
          ['Actuals report', 'Hours by code', '#2563eb'],
          ['Analytics', 'Reg · T12 · DBL', '#d97706'],
        ].map(([t, s, c]) => (
          <div key={t} style={{
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, textAlign: 'center',
          }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: c }}>→</div>
            <div style={{ fontWeight: 800, fontSize: 12, color: '#0f172a', marginTop: 4 }}>{t}</div>
            <div style={{ fontSize: 10, color: '#64748b' }}>{s}</div>
          </div>
        ))}
      </div>
      <div style={{
        background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: 12, fontSize: 12, color: '#065f46',
      }}>
        Description is looked up from the job’s op-code list and stamped on the hours row. Empty list? Ask Accounting to load it — do not invent a number.
      </div>
    </div>
  </WindowsChrome>
);

/* ------------------------------------------------------------------ */
/* Registry                                                            */
/* ------------------------------------------------------------------ */

export const FRAME_MAP = {
  hero: FrameHero,
  sidebar: FrameSidebar,
  matrix: FrameMatrix,
  freeze: FrameFreeze,
  shop: FrameShop,
  field: FrameField,
  excel: FrameExcel,
  io: FrameIO,
  trends: FrameTrends,
  collab: FrameCollab,
  schedule: FrameSchedule,
  analytics: FrameAnalytics,
  sisters: FrameSisters,
  reissue: FrameReissue,
  checklist: FrameChecklist,
  form: FrameForm,
  dashboard: FrameDashboard,
  gaHero: FrameGaHero,
  gaToolbar: FrameGaToolbar,
  gaEdit: FrameGaEdit,
  gaBalloons: FrameGaBalloons,
  gaPresent: FrameGaPresent,
  gaPress: FrameGaPress,
  ttHero: FrameTtHero,
  ttDailyWeekly: FrameTtDailyWeekly,
  ttPipeline: FrameTtPipeline,
  ttDropdown: FrameTtDropdown,
  ttAdmin: FrameTtAdmin,
  ttSaved: FrameTtSaved,
  fabHero: FrameFabHero,
  fabReload: FrameFabReload,
  fabPlanning: FrameFabPlanning,
  fabFilters: FrameFabFilters,
  fabPortfolio: FrameFabPortfolio,
  fabNext: FrameFabNext,
};

/**
 * Pick a frame key from title + speech text using keyword heuristics.
 */
export function pickFrameKey(text = '') {
  const t = String(text).toLowerCase();
  const rules = [
    [/matrix|grid|spreadsheet|inline edit|drag.?fill|column/, 'matrix'],
    [/sidebar|nav|menu|finding|where to find|left (pane|rail)/, 'sidebar'],
    [/import|export|excel (in|out)|getting data/, 'io'],
    [/chart|trend|history|analytics|report|dashboard|evm|earned/, 'analytics'],
    [/freeze|lock|rules of credit|weight/, 'freeze'],
    [/shop|fabricat|bench|tack|weld(ed)?|ship/, 'shop'],
    [/field|transport|fit.?up|bolt|install/, 'field'],
    [/form|details|edit record|save changes/, 'form'],
    [/schedule|activity|gantt|percent complete/, 'schedule'],
    [/checklist|first week|getting started|day.?one|habit/, 'checklist'],
    [/collab|comment|mention|hold|teammate|presence/, 'collab'],
    [/sister|nde|testing tracker|related view/, 'sisters'],
    [/re.?issue|supersed|extra line|struck/, 'reissue'],
    [/welcome|overview|tour|intro|heartbeat/, 'hero'],
    [/labour code|op code|opcode|where .+ come from|coding structure|maestro catalogue/, 'ttPipeline'],
    [/daily or weekly|crew head|timesheet/, 'ttDailyWeekly'],
    [/picking a code|dropdown|project-discipline/, 'ttDropdown'],
    [/standard codes|extras|inactive|admin/, 'ttAdmin'],
    [/after you save|payroll|actuals|analytics/, 'ttSaved'],
    [/hours that payroll|time tracker/, 'ttHero'],
  ];
  for (const [re, key] of rules) {
    if (re.test(t)) return key;
  }
  // spreadsheet-style editing without "matrix" wording
  if (/edit|filter|column layout/.test(t)) return 'excel';
  if (/dashboard|kpi|metric/.test(t)) return 'dashboard';
  return 'hero';
}
